// src/services/GroupWalkieService.js
/**
 * Group Walkie: a group chat's open call, peer to peer (docs/features/native_comms_suite_FEATURE.md Phase 4).
 *
 * The room (groupChats/{groupId}/walkie/room) is server-owned: start, join, beat and leave go through the
 * walkieGroupRoom callable, which caps it at 8 live and drops a phone that stopped beating. Media is a mesh: one
 * RTCPeerConnection per other live member, signalled through the same walkieSignals queue as 1:1 Walkie (protocol v2),
 * each pair's call id `${roomId}~${uidLow}~${uidHigh}` with a `roomId` field. The person joining offers to everyone
 * already live; a live member answers an offer for their room on its own, with no ring. A `room-invite` (written by
 * the server when a call starts) rings members who are not in it.
 */
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/services/firebaseconfig'
import { walkieService, isStaleSignal } from '@/services/WalkieService'

export const MAX_LIVE = 8
const BEAT_MS = 30 * 1000
const LEVEL_POLL_MS = 400

/** One pair's call id inside a room: the same on both ends. */
export const pairCallId = (roomId, a, b) => `${roomId}~${[a, b].sort().join('~')}`

class GroupWalkieService {
  constructor() {
    this.myUid = null
    this.room = null // { groupId, roomId }
    this.links = new Map() // peerUid -> { callId, pc, pending: [], remoteSet }
    this.localStream = null
    this.muted = false
    this.signalUnsubscribe = null
    this.roomUnsubscribe = null
    this.beatTimer = null
    this.levelTimer = null
    this._audioOwnership = null
    this._lease = null
    // Callbacks
    this.onInvite = null // ({ groupId, roomId, fromUid }) => {}
    this.onRoom = null // ({ groupId, roomId, live: [uid] }) => {}
    this.onStream = null // (uid, MediaStream | null) => {}
    this.onLevels = null // ({ [uid]: 0..1 }) => {}
    this.onEnded = null // (reason) => {}
    this.onError = null // (error) => {}
  }

  setAudioOwnership(ownership) {
    this._audioOwnership = ownership
  }

  startListening(myUid) {
    if (this.signalUnsubscribe) return
    this.myUid = myUid
    this.signalUnsubscribe = onSnapshot(
      query(collection(db, 'walkieSignals', myUid, 'incoming'), orderBy('createdAt', 'asc')),
      snapshot => {
        snapshot
          .docChanges()
          .filter(change => change.type === 'added')
          .map(change => ({ id: change.doc.id, ...change.doc.data() }))
          .filter(signal => signal.roomId)
          .forEach(signal => this._handleSignal(signal).catch(error => this.onError?.(error)))
      },
      error => console.warn('[GroupWalkie] signal listener error:', error)
    )
  }

  async stopListening() {
    this.signalUnsubscribe?.()
    this.signalUnsubscribe = null
    await this.leave()
    this.myUid = null
  }

  isInCall() {
    return Boolean(this.room)
  }

  /** Start a call in this group (nobody live) or join the one going (roomId). */
  async enter(groupId, roomId = null) {
    if (this.room) throw new Error('You are already in a group Walkie call.')
    // The microphone is one person's at a time in a tab: a 1:1 Walkie or company call holds it.
    if (this._audioOwnership)
      this._lease = this._audioOwnership.reserve('group', () => this.leave())
    try {
      await walkieService.fetchTurnCredentials()
      const result = await httpsCallable(
        functions,
        'walkieGroupRoom'
      )(roomId ? { action: 'join', groupId, roomId } : { action: 'start', groupId })
      const view = result.data?.room
      if (!view?.roomId) throw new Error('This call has ended.')
      this.room = { groupId, roomId: view.roomId }
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      })
      this._applyMute()
      this._watchRoom(groupId)
      this.onRoom?.({ groupId, roomId: view.roomId, live: view.live })
      for (const uid of view.live) if (uid !== this.myUid) await this._offer(uid)
      this.beatTimer = setInterval(() => this._beat(), BEAT_MS)
      this.levelTimer = setInterval(() => this._levels(), LEVEL_POLL_MS)
      return view
    } catch (error) {
      await this.leave({ quiet: true })
      throw error
    }
  }

  async leave({ quiet = false } = {}) {
    const room = this.room
    this.room = null
    clearInterval(this.beatTimer)
    clearInterval(this.levelTimer)
    this.beatTimer = this.levelTimer = null
    this.roomUnsubscribe?.()
    this.roomUnsubscribe = null
    for (const [uid, link] of [...this.links]) {
      if (room)
        this._send(uid, { type: 'hangup', callId: link.callId, roomId: room.roomId }).catch(
          () => {}
        )
      this._close(uid)
    }
    this.localStream?.getTracks().forEach(track => track.stop())
    this.localStream = null
    this._lease?.release()
    this._lease = null
    if (!room) return
    this._purgeRoom(room.roomId)
    try {
      await httpsCallable(
        functions,
        'walkieGroupRoom'
      )({ action: 'leave', groupId: room.groupId, roomId: room.roomId })
    } catch (e) {
      // The server drops an entry that stops beating within 90 seconds.
    }
    if (!quiet) this.onEnded?.('local_leave')
  }

  setMuted(muted) {
    this.muted = Boolean(muted)
    this._applyMute()
  }

  _applyMute() {
    this.localStream?.getAudioTracks().forEach(track => {
      track.enabled = !this.muted
    })
  }

  async _beat() {
    const room = this.room
    if (!room) return
    try {
      await httpsCallable(
        functions,
        'walkieGroupRoom'
      )({ action: 'beat', groupId: room.groupId, roomId: room.roomId })
    } catch (error) {
      const code = String(error?.code || '')
      if (
        this.room === room &&
        (code.endsWith('not-found') || code.endsWith('permission-denied'))
      ) {
        await this.leave({ quiet: true })
        this.onEnded?.('dropped')
      }
    }
  }

  _watchRoom(groupId) {
    this.roomUnsubscribe = onSnapshot(doc(db, 'groupChats', groupId, 'walkie', 'room'), snap => {
      const room = this.room
      if (!room) return
      const data = snap.exists() ? snap.data() : null
      const live = data?.roomId === room.roomId ? Object.keys(data.live || {}) : []
      if (!live.includes(this.myUid)) return
      // Someone left (or stopped beating): their connection goes. Newcomers offer to us.
      for (const uid of [...this.links.keys()]) if (!live.includes(uid)) this._close(uid)
      this.onRoom?.({ groupId, roomId: room.roomId, live })
    })
  }

  async _handleSignal(signal) {
    // Over: never acted on, and this queue's owner clears it (1:1 Walkie leaves group signals to this service).
    if (isStaleSignal(signal))
      return deleteDoc(doc(db, 'walkieSignals', this.myUid, 'incoming', signal.id)).catch(() => {})
    if (signal.type === 'room-invite') {
      if (this.room?.roomId !== signal.roomId)
        this.onInvite?.({ groupId: signal.groupId, roomId: signal.roomId, fromUid: signal.fromUid })
      return
    }
    const room = this.room
    if (
      !room ||
      signal.roomId !== room.roomId ||
      signal.callId !== pairCallId(room.roomId, this.myUid, signal.fromUid)
    )
      return
    if (signal.type === 'offer') return this._answer(signal)
    const link = this.links.get(signal.fromUid)
    if (!link) return
    if (signal.type === 'answer') {
      await link.pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(signal.payload)))
      await this._flush(link)
    } else if (signal.type === 'ice-candidate') {
      const candidate = JSON.parse(signal.payload)
      if (link.remoteSet) await link.pc.addIceCandidate(new RTCIceCandidate(candidate))
      else if (link.pending.length < 64) link.pending.push(candidate)
    } else if (signal.type === 'hangup') this._close(signal.fromUid)
  }

  _link(uid) {
    const room = this.room
    this._close(uid)
    const pc = new RTCPeerConnection({ iceServers: walkieService.iceServers })
    const link = {
      callId: pairCallId(room.roomId, this.myUid, uid),
      pc,
      pending: [],
      remoteSet: false,
    }
    this.localStream?.getTracks().forEach(track => pc.addTrack(track, this.localStream))
    pc.onicecandidate = event => {
      if (event.candidate && this.links.get(uid) === link) {
        this._send(uid, {
          type: 'ice-candidate',
          callId: link.callId,
          roomId: room.roomId,
          payload: JSON.stringify(event.candidate),
        })
      }
    }
    pc.ontrack = event => {
      if (this.links.get(uid) === link) this.onStream?.(uid, event.streams[0])
    }
    pc.onconnectionstatechange = () => {
      if (this.links.get(uid) === link && pc.connectionState === 'failed') this._close(uid)
    }
    this.links.set(uid, link)
    return link
  }

  async _offer(uid) {
    const link = this._link(uid)
    const offer = await link.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    })
    await link.pc.setLocalDescription(offer)
    if (!this.room) return
    await this._send(uid, {
      type: 'offer',
      callId: link.callId,
      roomId: this.room.roomId,
      payload: JSON.stringify(offer),
    })
  }

  async _answer(signal) {
    const link = this._link(signal.fromUid)
    await link.pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(signal.payload)))
    await this._flush(link)
    const answer = await link.pc.createAnswer()
    await link.pc.setLocalDescription(answer)
    if (!this.room) return
    await this._send(signal.fromUid, {
      type: 'answer',
      callId: link.callId,
      roomId: this.room.roomId,
      payload: JSON.stringify(answer),
    })
  }

  async _flush(link) {
    link.remoteSet = true
    for (const candidate of link.pending.splice(0))
      await link.pc.addIceCandidate(new RTCIceCandidate(candidate))
  }

  _close(uid) {
    const link = this.links.get(uid)
    if (!link) return
    this.links.delete(uid)
    link.pc.close()
    this.onStream?.(uid, null)
  }

  /** Who is talking: each connection's incoming audio level. */
  async _levels() {
    const levels = {}
    for (const [uid, link] of this.links) {
      try {
        const stats = await link.pc.getStats()
        stats.forEach(stat => {
          const audio = stat.kind === 'audio' || stat.mediaType === 'audio'
          if (stat.type === 'inbound-rtp' && audio && typeof stat.audioLevel === 'number')
            levels[uid] = stat.audioLevel
        })
      } catch (e) {
        // closed under the poll
      }
    }
    this.onLevels?.(levels)
  }

  async _send(toUid, data) {
    await setDoc(doc(collection(db, 'walkieSignals', toUid, 'incoming')), {
      ...data,
      fromUid: this.myUid,
      createdAt: serverTimestamp(),
    })
  }

  /** Clears this room's signals from my own queue once I have left it. */
  async _purgeRoom(roomId) {
    if (!this.myUid) return
    try {
      const snap = await getDocs(
        query(
          collection(db, 'walkieSignals', this.myUid, 'incoming'),
          where('roomId', '==', roomId)
        )
      )
      const batch = writeBatch(db)
      snap.docs.forEach(d => batch.delete(d.ref))
      await batch.commit()
    } catch (e) {
      // the stale sweep clears it later
    }
  }
}

export const groupWalkieService = new GroupWalkieService()
export default groupWalkieService
